import * as R from "ramda";
import React, { useEffect, useRef, useState } from "react";
import FlexView from "react-flexview";
import DefaultImage from "../assets/img/upload img.jpg";
import SimpleButton from "./commons/SimpleButton";
import SimpleModal from "./popup/SimpleModal";
import { ClipLoader } from "react-spinners";

export default function SimpleCamera({
  value,
  shape,
  onChange,
  enableUpload,
  maxSizes,
  compressed,
  loading,
  ...props
}) {
  const [image, setImage] = useState(DefaultImage);
  const [cameraModalVisible, setCameraModalVisible] = useState(false);
  const [mediaStream, setMediaStream] = useState(null);
  const [error, setError] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const shapeStyle = R.equals(shape, "circle") ? "circle" : "default";

  useEffect(() => {
    if (R.isNil(value) || R.isEmpty(value)) {
      setImage(DefaultImage);
    } else {
      setImage(value);
    }
  }, [value]);

  const handleOpenCamera = async () => {
    try {
      if (props.disabled) {
        return;
      }
      setCameraModalVisible(true);
      await openCamera();
    } catch (error) {
      console.error("Error opening camera:", error);
    }
  };

  const openCamera = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: "environment" } },
      audio: false,
    });

    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.onloadedmetadata = () => videoRef.current.play();
      setMediaStream(stream);
    }
  };

  const stopCamera = () => {
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop());
      setMediaStream(null);
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setCameraModalVisible(false);
  };

  const handleTakePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const context = canvasRef.current.getContext("2d");
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    context.drawImage(
      videoRef.current,
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height
    );

    canvasRef.current.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], "captured-photo.png", {
        type: "image/png",
      });
      convertBlobToBase64(file, props.name);
    }, "image/png");

    stopCamera();
  };

  const convertBlobToBase64 = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof onChange === "function") {
        onChange(reader.result, props.name);
      }
      setImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const compressTo1MB = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          const maxDimension = 1920;
          if (width > height && width > maxDimension) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          } else if (height > maxDimension) {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          let quality = 0.9;
          const step = 0.05;

          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (blob.size <= 1024 * 1024 || quality <= 0.1) {
                  resolve(new File([blob], file.name, { type: "image/jpeg" }));
                } else {
                  quality -= step;
                  tryCompress();
                }
              },
              "image/jpeg",
              quality
            );
          };

          tryCompress();
        };
      };
      reader.readAsDataURL(file);
    });
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError(""); 

    let finalFile = file;
    if (compressed && file.size > 1024 * 1024) {
      finalFile = await compressTo1MB(file);
    }

    // 🔹 convert ke base64
    convertBlobToBase64(finalFile);
  };

  return (
    <div className={`margin-bottom-20px ${props.containerclassname}`}>
      <div className="margin-5px">
        <div className="txt-site txt-11 txt-main txt-bold">
          <h4>
            {props.label}{" "}
            {props.mandatory && <span style={{ color: "red" }}>*</span>}
          </h4>
        </div>
      </div>
      <FlexView column hAlignContent="center" style={{ gap: "8px" }}>
        {loading ? (
          <ClipLoader size={50} color="#123abc" loading={loading} /> // 🔄 spinner
        ) : (
          <img
            src={image}
            className={`upload-pict ${shapeStyle}`}
            style={{ objectFit: "contain", width: "100%", height: "100%" }}
          />
        )}
        {!props.disabled && (
          <>
            <FlexView
              hAlignContent={"right"}
              style={{ alignItems: "center", alignSelf: "end" }}
            >
              {enableUpload && (
                <div>
                  <input
                    {...props}
                    type="file"
                    accept={props.accept ? props.accept : ".jpg,.jpeg,.png"}
                    onChange={handleUploadImage}
                    hidden
                  />
                  <div>
                    <label
                      className="txt-site txt-11 txt-main txt-bold"
                      style={{
                        paddingTop: "12px",
                        paddingBottom: "12px",
                        paddingLeft: "25px",
                        paddingRight: "25px",
                        display: "block",
                        backgroundColor: " #eeeeee",
                        cursor: "pointer",
                      }}
                      htmlFor={props.id}
                    >
                      Upload Photo
                    </label>
                  </div>
                </div>
              )}
              {!loading &&
              <SimpleButton
                secondary
                text="Take Photo"
                type="button"
                onClick={handleOpenCamera}
              />}
            </FlexView>
          </>
        )}
      </FlexView>

      {(props.mandatory && props.errorMessage) || error ? (
        <p className="txt-site txt-11 color-red margin-top-5px">
          {props.errorMessage || error}
        </p>
      ) : null}

      {cameraModalVisible && (
        <Camera
          onClickClose={() => stopCamera()}
          visible={cameraModalVisible}
          videoRef={videoRef}
          canvasRef={canvasRef}
          handleTakePhoto={handleTakePhoto}
        />
      )}
    </div>
  );
}

function Camera(props) {
  return (
    <SimpleModal
      visible={props.visible}
      onClickClose={() => props.onClickClose()}
      styles={{ width: "650px" }}
      content={
        <>
          <video
            ref={props.videoRef}
            autoPlay
            playsInline
            style={{ width: "100%", height: "100%" }}
          />
          <canvas ref={props.canvasRef} style={{ display: "none" }} />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <i
              role="button"
              className="fa fa-camera"
              onClick={props.handleTakePhoto}
              style={{
                fontSize: "60px",
                position: "absolute",
                color: "white",
                bottom: "20px",
                cursor: "pointer",
              }}
            ></i>
          </div>
        </>
      }
    />
  );
}
